/**
 * The active view selection (S15-live). `null` = the default board (full model,
 * identity projection). Setting a name activates that view's projection — the
 * board re-renders through `planView` → `applyProjection` (LayoutProvider wires
 * it; the navigation panel / a view tab sets it — there is no board view
 * dropdown). View state is session-ephemeral (design §3).
 */
import { createContext, useContext } from "react";
import type { ViewDeclV2 } from "@archlang/engine";

export interface ActiveViewApi {
  readonly activeViewName: string | null;
  readonly setActiveViewName: (name: string | null) => void;
  /** Session-ephemeral plane pick (S16) — an `on <plane>` subset that composes
   *  onto the active view. Null = all planes (whole model). */
  readonly activePlane: string | null;
  readonly setActivePlane: (plane: string | null) => void;
  /** Live knob-picker values (S18) — override a view's knob defaults; fed to
   *  the projection as instance bindings. PER-VIEW keyed (effective view name;
   *  drafts under `::draft`), then by knob name — a session-global map would
   *  leak view A's `depth` into view B's same-named knob (render AND persist). */
  readonly knobsByView: Readonly<Record<string, Readonly<Record<string, number | string>>>>;
  readonly setKnob: (viewKey: string, name: string, value: number | string) => void;
  /** An in-browser DRAFT view (the WYSIWYG builder's unsaved tier). When set
   *  it takes precedence over `activeViewName` in the projection — the board
   *  renders a view that exists nowhere in the model text until saved.
   *  `sessionKey` identifies the draft SESSION (the builder tab): crossing a
   *  session boundary drops the shared `::draft` knob/reveal state, while a
   *  rename WITHIN one session (same key) keeps it. Omitted → falls back to
   *  the draft's name (name change = boundary). */
  readonly draftView: ViewDeclV2 | null;
  readonly setDraftView: (view: ViewDeclV2 | null, sessionKey?: string | null) => void;
  /** Click-reveal (VIEWS §7): PER-VIEW session-ephemeral reveal sets, keyed
   *  by the effective view name (drafts under the reserved `::draft` key).
   *  Per-view keying is load-bearing — a session-global set would leak view
   *  A's reveals into view B's selection. */
  readonly revealsByView: Readonly<Record<string, readonly string[]>>;
  readonly revealKey: (viewKey: string, qualifiedKey: string) => void;
  readonly clearReveals: (viewKey: string) => void;
}

export const ActiveViewContext = createContext<ActiveViewApi>({
  activeViewName: null,
  setActiveViewName: () => {},
  activePlane: null,
  setActivePlane: () => {},
  knobsByView: {},
  setKnob: () => {},
  draftView: null,
  setDraftView: () => {},
  revealsByView: {},
  revealKey: () => {},
  clearReveals: () => {},
});

export const useActiveView = (): ActiveViewApi => useContext(ActiveViewContext);
